/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 4/28/22, 1:27 PM
 *
 */

import React, { Fragment, useEffect } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid';
import { useAppDispatch, useAppSelector } from 'stores';
import { RoleAccess } from 'types/RoleTypes';

import { setRole } from 'features/members';

interface MemberRoleDropdownProps {
  roleList?: RoleAccess[];
  selectedRole?: RoleAccess;
  setSelectedRole?: (selected_role: RoleAccess) => void;
}

export const MemberRoleDropdown = ({
  roleList: _roleList,
  selectedRole: _selectedRole,
  setSelectedRole: _setSelectedRole,
}: MemberRoleDropdownProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const roleList = _roleList ?? useAppSelector((state) => state.auth.user?.role?.role_access);
  const selectedRole = useAppSelector((state) => state.members?.role);

  useEffect(() => {
    selectedRole &&
      Object.keys(selectedRole).length !== 0 &&
      navigate({
        pathname: location.pathname,
        hash: location.hash,
        search: createSearchParams({
          page: '1',
        }).toString(),
      });
  }, [selectedRole?.slug, _selectedRole?.slug]);

  return (
    <div className="z-10 w-64 capitalize">
      <Listbox
        value={_selectedRole ?? selectedRole}
        onChange={(value) => {
          value && dispatch(setRole(value));
        }}
      >
        <div className="relative">
          <Listbox.Button
            data-testid="role-dropdown-btn"
            className="relative w-full cursor-pointer rounded-md bg-white py-5 px-6 text-left text-xl font-medium text-gray-500 shadow-sm ring-1 ring-gray-300 focus:outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-300"
          >
            <span className="block truncate">
              {_selectedRole && Object.values(_selectedRole).length !== 0
                ? _selectedRole.name
                : selectedRole?.name}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Listbox.Options className="absolute mt-2 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-lg shadow-sm ring-1 ring-black ring-opacity-5 focus:outline-none">
              {roleList?.map((role) => (
                <Listbox.Option
                  key={role.id}
                  data-testid={`${role.name}-btn`}
                  className={({ active }) =>
                    `${active ? 'bg-gray-50 text-primary-500' : 'text-gray-900'}
                          relative cursor-pointer select-none py-4 pl-10 pr-4 `
                  }
                  value={role}
                >
                  {() => {
                    return (
                      <>
                        <span
                          className={`${
                            (_selectedRole
                              ? role.id === _selectedRole?.id
                              : role.id === selectedRole?.id) && 'text-primary-500'
                          } block truncate capitalize`}
                        >
                          {role.name}
                        </span>
                        {(
                          _selectedRole
                            ? role.id === _selectedRole?.id
                            : role.id === selectedRole?.id
                        ) ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    );
                  }}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};
